import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Permission, RoleInput } from '../types';

interface RoleFormProps {
  permissions: Permission[];
  isSubmitting: boolean;
  isDisabled: boolean;
  onSubmit: (input: RoleInput) => Promise<boolean>;
}

const EMPTY_ROLE: RoleInput = { name: '', description: '', permissionIds: [] };

const RoleForm: React.FC<RoleFormProps> = ({
  permissions,
  isSubmitting,
  isDisabled,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<RoleInput>(EMPTY_ROLE);

  const handlePermissionChange = (permissionId: number, checked: boolean) => {
    setValues((current) => ({
      ...current,
      permissionIds: checked
        ? [...current.permissionIds, permissionId]
        : current.permissionIds.filter((currentId) => currentId !== permissionId),
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const didSave = await onSubmit({
      ...values,
      name: values.name.trim(),
      description: values.description.trim(),
    });
    if (didSave) setValues(EMPTY_ROLE);
  };

  return (
    <section className="h-full rounded-[20px] border border-outline-variant/70 bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-4">
        <h2 className="font-h3 text-h3 text-on-surface">
          {t('admin.settings.role_form.title')}
        </h2>
        <p className="mt-1 text-body-sm text-secondary">
          {t('admin.settings.role_form.description')}
        </p>
      </div>

      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <label className="flex flex-col gap-1.5 font-label-md text-on-surface">
          {t('admin.settings.fields.role_name')}
          <input
            required
            pattern="[A-Z][A-Z0-9_]*"
            title={t('admin.settings.validation.role_name')}
            className="h-11 rounded-xl border border-outline-variant bg-white px-3 font-body-md text-body-md font-normal focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
            placeholder="STAFF"
            value={values.name}
            onChange={(event) => setValues((current) => ({
              ...current,
              name: event.target.value.toUpperCase(),
            }))}
          />
        </label>

        <label className="flex flex-col gap-1.5 font-label-md text-on-surface">
          {t('admin.settings.fields.description')}
          <textarea
            required
            rows={3}
            className="resize-y rounded-xl border border-outline-variant bg-white px-3 py-2 font-body-md text-body-md font-normal focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
            placeholder={t('admin.settings.role_form.description_placeholder')}
            value={values.description}
            onChange={(event) => setValues((current) => ({
              ...current,
              description: event.target.value,
            }))}
          />
        </label>

        <fieldset>
          <legend className="mb-1.5 font-label-md text-on-surface">
            {t('admin.settings.fields.initial_permissions')}
          </legend>
          <div className="max-h-40 overflow-y-auto rounded-xl border border-outline-variant bg-surface-container-lowest p-2">
            {permissions.length === 0 ? (
              <p className="px-2 py-3 text-center text-body-sm text-secondary">
                {t('admin.settings.role_form.no_permissions')}
              </p>
            ) : permissions.map((permission) => (
              <label
                key={permission.id}
                className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-surface-container-low"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-primary"
                  checked={values.permissionIds.includes(permission.id)}
                  onChange={(event) => handlePermissionChange(permission.id, event.target.checked)}
                />
                <span>
                  <span className="block font-mono text-[12px] font-semibold text-on-surface">
                    {permission.name}
                  </span>
                  <span className="block text-body-sm text-secondary">{permission.description}</span>
                </span>
              </label>
            ))}
          </div>
          <p className="mt-1 text-body-sm text-secondary">
            {t('admin.settings.role_form.selected_count', { count: values.permissionIds.length })}
          </p>
        </fieldset>

        <div>
          <button
            type="submit"
            disabled={isDisabled}
            className="min-h-11 rounded-xl bg-primary px-4 font-label-md text-on-primary transition-colors hover:bg-on-primary-fixed-variant disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting
              ? t('admin.settings.actions.creating')
              : t('admin.settings.actions.create_role')}
          </button>
        </div>
      </form>
    </section>
  );
};

export default RoleForm;
